// ProposalsPPDDropdownsAdmin.js
// ✅ Same file, same UI/structure — ONLY changes needed for ProposalsPPD collections + headings

import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  deleteDoc
} from "firebase/firestore";
import { db } from "../firebaseConfig";

const ButtonSpinner = () => (
  <span
    aria-hidden="true"
    style={{
      width: 15,
      height: 15,
      border: "2px solid rgba(255,255,255,0.55)",
      borderTopColor: "#ffffff",
      borderRadius: "50%",
      display: "inline-block",
      animation: "proposal-ppd-spin 0.75s linear infinite",
      flexShrink: 0,
    }}
  />
);

const ProposalsPPDDropdownsAdmin = () => {
  const [dropdowns, setDropdowns] = useState([]);
  const [newDropdown, setNewDropdown] = useState({
    label: "",
    fieldName: "",
    options: []
  });
  const [busyAction, setBusyAction] = useState("");
  const [editingWarrantyId, setEditingWarrantyId] = useState(null);
  const [editingWarranty, setEditingWarranty] = useState({ name: "", content: "" });
  const [editingTermId, setEditingTermId] = useState(null);
  const [editingTerm, setEditingTerm] = useState({ name: "", content: "" });

  // Existing saved-item containers start hidden.
  // The add/edit forms remain visible at all times.
  const [showDropdownContainers, setShowDropdownContainers] = useState(false);
  const [showWarrantyContainers, setShowWarrantyContainers] = useState(false);
  const [showTermContainers, setShowTermContainers] = useState(false);

  // ================== PAGE-LOCAL STYLES ==================
  const pageContainer = {
    padding: 20,
    maxWidth: 1100,
    margin: "0 auto",
  };

  const sectionCard = {
    border: "1px solid #1d4ed8",
    borderRadius: 12,
    padding: 16,
    background: "#fff",
    marginBottom: 20,
  };

  const headerRow = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  };

  const tabButton = (active = false) => ({
    padding: "10px 14px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    borderBottom: active ? "1px solid white" : "1px solid #1d4ed8",
    background: active ? "#1d4ed8" : "#93c5fd",
    color: active ? "white" : "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  });

  const actionButton = {
    padding: "8px 14px",
    borderRadius: 8,
    border: "1px solid #1e3a8a",
    background: "#1d4ed8",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minHeight: 38,
  };

  const disabledButton = {
    opacity: 0.7,
    cursor: "not-allowed",
  };

  const toggleButton = {
    ...actionButton,
    background: "#1e3a8a",
    border: "1px solid #172554",
    marginBottom: 12,
  };

  const dangerButton = {
    ...actionButton,
    border: "1px solid #dc2626",
    background: "#fee2e2",
    color: "#991b1b",
  };

  const inputStyle = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #1d4ed8",
    minWidth: 240,
  };

  const listItemBox = {
    border: "1px solid #1d4ed8",
    borderRadius: 10,
    padding: 12,
    marginBottom: 10,
    background: "#f8fafc",
  };

  // ✅ CHANGED collections from invoice* → proposalsPPD*
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState({ name: "", content: "" });

  const [warranties, setWarranties] = useState([]);
  const [newWarranty, setNewWarranty] = useState({ name: "", content: "" });

  const [selectedDropdown, setSelectedDropdown] = useState(null); // kept (unused)
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedDropdownId, setExpandedDropdownId] = useState(null);
// ✅ NEW: paragraph inputs for options/sub-options
const [bulkSubOptionText, setBulkSubOptionText] = useState("");
const [newOptionTextByDropdown, setNewOptionTextByDropdown] = useState({});
const [newSubOptionTextByKey, setNewSubOptionTextByKey] = useState({});
const [newOptionTitleByDropdown, setNewOptionTitleByDropdown] = useState({});

const serviceTypes = [
  "Action Items",
  "Inspection",
  "Meeting",
  "Proposal",
  "One-Time Treatment",
  "BED - Bed Bug Treatment",
  "HTT - Home Termite Treatment",
  "MPC - Monthly Pest Control",
  "QPC - Quarterly Pest Control",
  "RBS - Rodent Bait Station Service",
  "SEN - Termite Monitoring Station Service",
  "STT - Settlement Termite Treatment",
  "WDI - Wood Destroying Insect Inspection",
  "WLW - Wildlife Removal/Exclusion",
  "WPC - Warranty Pest Control",
  "WYG - Warranty Yard Guard",
  "YG - Yard Guard",
];

const [serviceTypeTemplates, setServiceTypeTemplates] = useState([]);
const [selectedServiceType, setSelectedServiceType] = useState("");
const [isAddingServiceType, setIsAddingServiceType] = useState(false);
const [newServiceTypeName, setNewServiceTypeName] = useState("");
const [serviceTypeTitle, setServiceTypeTitle] = useState("");
const [serviceTypeContent, setServiceTypeContent] = useState("");
const [serviceTypeTotal, setServiceTypeTotal] = useState("");
const [serviceTypeInitialAmount, setServiceTypeInitialAmount] = useState("");
const [serviceTypeFeeSchedule, setServiceTypeFeeSchedule] = useState("Monthly");

  const fetchWarranties = async () => {
    const snap = await getDocs(collection(db, "proposalsPPDWarranties")); // ✅ CHANGED
    setWarranties(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchTerms = async () => {
    const snap = await getDocs(collection(db, "proposalsPPDTerms")); // ✅ CHANGED
    setTerms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchServiceTypeTemplates = async () => {
    const snap = await getDocs(collection(db, "proposalsPPDServiceTypes"));
    setServiceTypeTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }; 
  
  useEffect(() => {
    fetchWarranties();
    fetchTerms();
    fetchDropdowns();
    fetchServiceTypeTemplates();
  }, []);

  const fetchDropdowns = async () => {
    const snap = await getDocs(collection(db, "proposalsPPDDropdowns")); // ✅ CHANGED
    const data = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order || 0) - (b.order || 0)); // ✅ safer
    setDropdowns(data);
  };

  const addDropdown = async () => {
    if (!newDropdown.label || !newDropdown.fieldName || busyAction) return;

    setBusyAction("addDropdown");
    try {
      await addDoc(collection(db, "proposalsPPDDropdowns"), {
        label: newDropdown.label,
        fieldName: newDropdown.fieldName,
        options: [],
        order: dropdowns.length + 1
      });

      setNewDropdown({ label: "", fieldName: "", options: [] });
      await fetchDropdowns();
    } finally {
      setBusyAction("");
    }
  };

  const addSubOption = async (dropdownId, optionIndex, value) => {
    const dropdown = dropdowns.find(d => d.id === dropdownId);
    if (!dropdown) return;

    const updatedOptions = [...(dropdown.options || [])];

    // ✅ guard in case option doesn't exist yet
    if (!updatedOptions[optionIndex]) return;

    updatedOptions[optionIndex].subOptions = [
      ...(updatedOptions[optionIndex].subOptions || []),
      value
    ];

    await updateDoc(doc(db, "proposalsPPDDropdowns", dropdownId), { // ✅ CHANGED
      options: updatedOptions
    });

    fetchDropdowns();
  };

  const handleServiceTypeSelect = (value) => {
    if (value === "__ADD_NEW_SERVICE_TYPE__") {
      setIsAddingServiceType(true);
      setSelectedServiceType("");
      setNewServiceTypeName("");
      setServiceTypeTitle("");
      setServiceTypeContent("");
      setServiceTypeTotal("");
      setServiceTypeInitialAmount("");
      setServiceTypeFeeSchedule("Monthly");
      return;
    }

    setIsAddingServiceType(false);
    setNewServiceTypeName("");
    setSelectedServiceType(value);
    setServiceTypeTitle(value);

    const existing = serviceTypeTemplates.find(
      item => item.serviceType === value
    );

    // Load the saved description and total so the template can be reviewed or edited.
    setServiceTypeContent(existing?.content || "");
    setServiceTypeTotal(existing?.total ?? "");
    setServiceTypeInitialAmount(existing?.initialAmount ?? "");
    setServiceTypeFeeSchedule(existing?.feeSchedule || "Monthly");
  };

  const move = async (id, dir) => {
    const idx = dropdowns.findIndex(d => d.id === id);
    const swap = dropdowns[idx + dir];
    if (!swap) return;

    await updateDoc(doc(db, "proposalsPPDDropdowns", id), { order: swap.order }); // ✅ CHANGED
    await updateDoc(doc(db, "proposalsPPDDropdowns", swap.id), { order: dropdowns[idx].order }); // ✅ CHANGED
    fetchDropdowns();
  };

  const deleteDropdown = async (id) => {
    await deleteDoc(doc(db, "proposalsPPDDropdowns", id)); // ✅ CHANGED
    setConfirmDelete(null);
    setSelectedDropdown(null);
    fetchDropdowns();
  };
  const addOptionParagraph = async (dropdownId) => {
    const dropdown = dropdowns.find(d => d.id === dropdownId);
    if (!dropdown) return;
  
    const title = (newOptionTitleByDropdown[dropdownId] || "").trim();
    const paragraph = (newOptionTextByDropdown[dropdownId] || "").trim();
  
    if (!title || !paragraph) return;
  
    const updated = [
      ...(dropdown.options || []),
      { title, value: paragraph, subOptions: [] }
    ];
  
    await updateDoc(doc(db, "proposalsPPDDropdowns", dropdownId), {
      options: updated
    });
  
    setNewOptionTitleByDropdown(prev => ({ ...prev, [dropdownId]: "" }));
    setNewOptionTextByDropdown(prev => ({ ...prev, [dropdownId]: "" }));
    fetchDropdowns();
  };
  
  const saveServiceTypeTemplate = async () => {
    const serviceTypeName = (
      isAddingServiceType ? newServiceTypeName : serviceTypeTitle
    ).trim();

    if (!serviceTypeName || busyAction) return;

    setBusyAction("saveServiceType");
    try {
      // When editing, locate the original selected record so its title can be renamed.
      const selectedExisting = serviceTypeTemplates.find(
        item => item.serviceType === selectedServiceType
      );

      const duplicateTitle = serviceTypeTemplates.find(
        item =>
          item.id !== selectedExisting?.id &&
          item.serviceType?.trim().toLowerCase() === serviceTypeName.toLowerCase()
      );

      if (duplicateTitle) {
        alert("A Service Type with this title already exists.");
        return;
      }

      if (selectedExisting) {
        await updateDoc(doc(db, "proposalsPPDServiceTypes", selectedExisting.id), {
          serviceType: serviceTypeName,
          content: serviceTypeContent,
          total: serviceTypeTotal === "" ? "" : Number(serviceTypeTotal),
          initialAmount: serviceTypeInitialAmount === "" ? "" : Number(serviceTypeInitialAmount),
          feeSchedule: serviceTypeFeeSchedule || "Monthly"
        });
      } else {
        await addDoc(collection(db, "proposalsPPDServiceTypes"), {
          serviceType: serviceTypeName,
          content: serviceTypeContent,
          total: serviceTypeTotal === "" ? "" : Number(serviceTypeTotal),
          initialAmount: serviceTypeInitialAmount === "" ? "" : Number(serviceTypeInitialAmount),
          feeSchedule: serviceTypeFeeSchedule || "Monthly"
        });
      }

      await fetchServiceTypeTemplates();
      setSelectedServiceType(serviceTypeName);
      setServiceTypeTitle(serviceTypeName);
      setIsAddingServiceType(false);
      setNewServiceTypeName("");
    } finally {
      setBusyAction("");
    }
  };
  const addSubOptionParagraph = async (dropdownId, optionIndex) => {
    const dropdown = dropdowns.find(d => d.id === dropdownId);
    if (!dropdown) return;
  
    const key = `${dropdownId}__${optionIndex}`;
    const paragraph = (newSubOptionTextByKey[key] || "").trim();
    if (!paragraph) return;
  
    const updatedOptions = [...(dropdown.options || [])];
    if (!updatedOptions[optionIndex]) return;
  
    updatedOptions[optionIndex].subOptions = [
      ...(updatedOptions[optionIndex].subOptions || []),
      paragraph
    ];
  
    await updateDoc(doc(db, "proposalsPPDDropdowns", dropdownId), {
      options: updatedOptions
    });
  
    setNewSubOptionTextByKey(prev => ({ ...prev, [key]: "" }));
    fetchDropdowns();
  };
  
  const addBulkSubOptionParagraph = async (dropdown) => {
    const paragraph = (bulkSubOptionText || "").trim();
    if (!paragraph) return;
  
    const updatedOptions = (dropdown.options || []).map(opt => ({
      ...opt,
      subOptions: [...(opt.subOptions || []), paragraph]
    }));
  
    await updateDoc(doc(db, "proposalsPPDDropdowns", dropdown.id), {
      options: updatedOptions
    });
  
    setBulkSubOptionText("");
    fetchDropdowns();
  };
  
  const addWarranty = async () => {
    if (!newWarranty.name.trim() || busyAction) return;

    setBusyAction("addWarranty");
    try {
      await addDoc(collection(db, "proposalsPPDWarranties"), newWarranty);
      setNewWarranty({ name: "", content: "" });
      await fetchWarranties();
    } finally {
      setBusyAction("");
    }
  };

  const saveWarrantyEdit = async (id) => {
    if (!editingWarranty.name.trim() || busyAction) return;

    setBusyAction(`saveWarranty-${id}`);
    try {
      await updateDoc(doc(db, "proposalsPPDWarranties", id), editingWarranty);
      setEditingWarrantyId(null);
      setEditingWarranty({ name: "", content: "" });
      await fetchWarranties();
    } finally {
      setBusyAction("");
    }
  };

  const addTerm = async () => {
    if (!newTerm.name.trim() || busyAction) return;

    setBusyAction("addTerm");
    try {
      await addDoc(collection(db, "proposalsPPDTerms"), newTerm);
      setNewTerm({ name: "", content: "" });
      await fetchTerms();
    } finally {
      setBusyAction("");
    }
  };

  const saveTermEdit = async (id) => {
    if (!editingTerm.name.trim() || busyAction) return;

    setBusyAction(`saveTerm-${id}`);
    try {
      await updateDoc(doc(db, "proposalsPPDTerms", id), editingTerm);
      setEditingTermId(null);
      setEditingTerm({ name: "", content: "" });
      await fetchTerms();
    } finally {
      setBusyAction("");
    }
  };


  const availableServiceTypes = Array.from(
    new Set([
      ...serviceTypes,
      ...serviceTypeTemplates
        .map(item => item.serviceType)
        .filter(Boolean)
    ])
  ).sort((a, b) => a.localeCompare(b));

  return (
    <div style={pageContainer}>
      <style>{`
        @keyframes proposal-ppd-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      {/* ✅ CHANGED title */}
      <h2>Proposals PPD Dropdown Builder</h2>

      <div style={sectionCard}>
        <h3>Add Dropdown</h3>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input
            style={inputStyle}
            placeholder="Label (ex: Service Type)"
            value={newDropdown.label}
            onChange={e =>
              setNewDropdown({ ...newDropdown, label: e.target.value })
            }
          />

          <input
            style={inputStyle}
            placeholder="Field Name (ex: serviceType)"
            value={newDropdown.fieldName}
            onChange={e =>
              setNewDropdown({ ...newDropdown, fieldName: e.target.value })
            }
          />

          <button
            style={{ ...actionButton, ...(busyAction ? disabledButton : {}) }}
            onClick={addDropdown}
            disabled={Boolean(busyAction)}
          >
            {busyAction === "addDropdown" && <ButtonSpinner />}
            {busyAction === "addDropdown" ? "Adding..." : "Add"}
          </button>
        </div>
      </div>

      <hr />

      <button
        type="button"
        style={toggleButton}
        onClick={() => setShowDropdownContainers(prev => !prev)}
      >
        {showDropdownContainers ? "Hide Saved Dropdown Containers" : "Show Saved Dropdown Containers"}
      </button>

      {showDropdownContainers && dropdowns.map(d => (
        <div key={d.id} style={sectionCard}>
          <div style={headerRow}>
            <button
              style={tabButton(expandedDropdownId === d.id)}
              onClick={() =>
                setExpandedDropdownId(expandedDropdownId === d.id ? null : d.id)
              }
            >
              {d.label}
            </button>

            <button
              style={dangerButton}
              onClick={() => setConfirmDelete(d)}
            >
              🗑 Delete
            </button>
          </div>

          {/* Optional move buttons kept (commented like you had) */}
          {/* <button onClick={() => move(d.id, -1)}>⬆</button>
              <button onClick={() => move(d.id, 1)}>⬇</button> */}

          {expandedDropdownId === d.id && (
            <div
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 10,
                background: "#f1f5f9",
                border: "1px dashed #1d4ed8",
              }}
            >
              {/* BULK SUB-OPTION */}
              {d.options?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <textarea
  style={{ ...inputStyle, width: "100%", minWidth: "100%" }}
  rows={3}
  placeholder="Add ONE sub-option paragraph to ALL options (multi-line allowed)"

  value={bulkSubOptionText}
  onChange={(e) => setBulkSubOptionText(e.target.value)}
/>

<button
  style={{ ...actionButton, marginTop: 8, ...(busyAction ? disabledButton : {}) }}
  type="button"
  onClick={() => addBulkSubOptionParagraph(d)}
  disabled={Boolean(busyAction)}
>
  {busyAction === `addBulkSubOption-${d.id}` && <ButtonSpinner />}
  {busyAction === `addBulkSubOption-${d.id}` ? "Adding..." : "Add Sub-Option(s) to All"}
</button>

                </div>
              )}

              {/* INDIVIDUAL OPTIONS */}
              <ul>
                {(d.options || []).map((opt, i) => (
                  <li key={i} style={listItemBox}>
                   <strong>{opt.title || opt.label}</strong>


                    {/* EXISTING SUB-OPTIONS */}
                    {opt.subOptions?.length > 0 && (
                      <ul>
                        {opt.subOptions.map((sub, j) => (
                          <li key={j}>{sub}</li>
                        ))}
                      </ul>
                    )}

                    {/* ADD SUB-OPTION TO ONE OPTION */}
                    <textarea
  style={{ ...inputStyle, width: "100%", minWidth: "100%", marginTop: 8 }}
  rows={3}
  placeholder="Add ONE sub-option paragraph (multi-line allowed)"

  value={newSubOptionTextByKey[`${d.id}__${i}`] || ""}
  onChange={(e) =>
    setNewSubOptionTextByKey((prev) => ({
      ...prev,
      [`${d.id}__${i}`]: e.target.value,
    }))
  }
/>

<button
  style={{ ...actionButton, marginTop: 8, ...(busyAction ? disabledButton : {}) }}
  type="button"
  onClick={() => addSubOptionParagraph(d.id, i)}
  disabled={Boolean(busyAction)}
>
  {busyAction === `addSubOption-${d.id}__${i}` && <ButtonSpinner />}
  {busyAction === `addSubOption-${d.id}__${i}` ? "Adding..." : "Add Sub-Option(s)"}
</button>

                  </li>
                ))}
              </ul>

              {/* ADD NEW OPTION */}
              <input
  style={{ ...inputStyle, width: "100%", minWidth: "100%", marginTop: 12 }}
  placeholder="Option Title (this shows in the dropdown list)"
  value={newOptionTitleByDropdown[d.id] || ""}
  onChange={(e) =>
    setNewOptionTitleByDropdown(prev => ({ ...prev, [d.id]: e.target.value }))
  }
/>

              <textarea
  style={{ ...inputStyle, width: "100%", minWidth: "100%", marginTop: 12 }}
  rows={3}
  placeholder="Option Paragraph (this is what inserts into Job Description — multi-line allowed)"

  value={newOptionTextByDropdown[d.id] || ""}
  onChange={(e) =>
    setNewOptionTextByDropdown((prev) => ({
      ...prev,
      [d.id]: e.target.value,
    }))
  }
/>

<button
  style={{ ...actionButton, marginTop: 8, ...(busyAction ? disabledButton : {}) }}
  type="button"
  onClick={() => addOptionParagraph(d.id)}
  disabled={Boolean(busyAction)}
>
  {busyAction === `addOption-${d.id}` && <ButtonSpinner />}
  {busyAction === `addOption-${d.id}` ? "Adding..." : "Add Option(s)"}
</button>

            </div>
          )}
        </div>
      ))}

      <hr />
      <hr />
      <hr />
<h3>Proposals PPD Service Type Templates</h3>

<div style={sectionCard}>
  <label style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
    Service Type
  </label>

  <select
    style={{ ...inputStyle, width: "100%", marginBottom: 12 }}
    value={isAddingServiceType ? "__ADD_NEW_SERVICE_TYPE__" : selectedServiceType}
    onChange={(e) => handleServiceTypeSelect(e.target.value)}
  >
    <option value="">Select Service Type</option>
    <option value="__ADD_NEW_SERVICE_TYPE__">+ Add New Service Type</option>
    {availableServiceTypes.map((type) => (
      <option key={type} value={type}>
        {type}
      </option>
    ))}
  </select>

  {isAddingServiceType ? (
    <>
      <label style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
        New Service Type Title
      </label>
      <input
        type="text"
        style={{ ...inputStyle, width: "100%", marginBottom: 12 }}
        placeholder="Enter the new Service Type title"
        value={newServiceTypeName}
        onChange={(e) => setNewServiceTypeName(e.target.value)}
      />
    </>
  ) : selectedServiceType ? (
    <>
      <label style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
        Service Type Title
      </label>
      <input
        type="text"
        style={{ ...inputStyle, width: "100%", marginBottom: 12 }}
        placeholder="Update the Service Type title"
        value={serviceTypeTitle}
        onChange={(e) => setServiceTypeTitle(e.target.value)}
      />
    </>
  ) : null}

  <label style={{ display: "block", fontWeight: 700, marginBottom: 8 }}>
    Description Content
  </label>

  <textarea
    style={{ ...inputStyle, width: "100%", minWidth: "100%" }}
    rows={8}
    placeholder="Enter the description text that should be inserted into the proposal page"
    value={serviceTypeContent}
    onChange={(e) => setServiceTypeContent(e.target.value)}
  />

  <label style={{ display: "block", fontWeight: 700, marginTop: 12, marginBottom: 8 }}>
    Default Service Total (optional)
  </label>
  <input
    type="number"
    min="0"
    step="0.01"
    style={{ ...inputStyle, width: "100%" }}
    placeholder="Leave blank when there is no standard price"
    value={serviceTypeTotal}
    onChange={(e) => setServiceTypeTotal(e.target.value)}
  />

<label style={{ display: "block", marginTop: 12, fontWeight: 700 }}>
  Initial Amount
</label>
<input
  type="number"
  min="0"
  step="0.01"
  value={serviceTypeInitialAmount}
  onChange={(e) => setServiceTypeInitialAmount(e.target.value)}
  placeholder="Default initial/start fee"
  style={{ ...inputStyle, width: "100%" }}
/>

<label style={{ display: "block", marginTop: 12, fontWeight: 700 }}>
  Fee Schedule
</label>
<select
  value={serviceTypeFeeSchedule}
  onChange={(e) => setServiceTypeFeeSchedule(e.target.value)}
  style={{ ...inputStyle, width: "100%" }}
>
  {["Monthly", "Quarterly", "Every 6 Months", "Yearly", "One-Time", "Other"].map((option) => (
    <option key={option} value={option}>{option}</option>
  ))}
</select>

  <button
    style={{ ...actionButton, marginTop: 10, ...(busyAction ? disabledButton : {}) }}
    onClick={saveServiceTypeTemplate}
    disabled={Boolean(busyAction)}
  >
    {busyAction === "saveServiceType" && <ButtonSpinner />}
    {busyAction === "saveServiceType"
      ? "Saving..."
      : isAddingServiceType
        ? "Add and Save Service Type"
        : serviceTypeTemplates.some(item => item.serviceType === selectedServiceType)
          ? "Update Service Type Template"
          : "Save Service Type Template"}
  </button>
</div>
      {/* ✅ CHANGED heading + collection */}
      <h3>Proposals PPD Warranties</h3>

      <input
        placeholder="Warranty Name"
        value={newWarranty.name}
        onChange={e => setNewWarranty({ ...newWarranty, name: e.target.value })}
      />

      <textarea
        placeholder="Warranty Content"
        rows={6}
        value={newWarranty.content}
        onChange={e => setNewWarranty({ ...newWarranty, content: e.target.value })}
      />

      <button
        style={{ ...actionButton, marginTop: 8, ...(busyAction ? disabledButton : {}) }}
        onClick={addWarranty}
        disabled={Boolean(busyAction)}
      >
        {busyAction === "addWarranty" && <ButtonSpinner />}
        {busyAction === "addWarranty" ? "Adding..." : "Add Warranty"}
      </button>

      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          style={toggleButton}
          onClick={() => setShowWarrantyContainers(prev => !prev)}
        >
          {showWarrantyContainers ? "Hide Saved Warranties" : "Show Saved Warranties"}
        </button>
      </div>

      {showWarrantyContainers && (
      <ul style={{ marginTop: 12 }}>
        {warranties.length === 0 && (
          <li style={{ fontStyle: "italic", color: "#64748b" }}>
            No warranties added yet.
          </li>
        )}

        {warranties.map(w => (
          <li
            key={w.id}
            style={{
              border: "1px solid #1d4ed8",
              borderRadius: 10,
              padding: 12,
              marginBottom: 10,
              background: "#f8fafc",
              cursor: "pointer",
            }}
          >
            {editingWarrantyId === w.id ? (
              <>
                <input
                  style={{ ...inputStyle, width: "100%", minWidth: "100%" }}
                  value={editingWarranty.name}
                  onChange={(e) =>
                    setEditingWarranty(prev => ({ ...prev, name: e.target.value }))
                  }
                />
                <textarea
                  style={{ ...inputStyle, width: "100%", minWidth: "100%", marginTop: 8 }}
                  rows={6}
                  value={editingWarranty.content}
                  onChange={(e) =>
                    setEditingWarranty(prev => ({ ...prev, content: e.target.value }))
                  }
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    style={{ ...actionButton, ...(busyAction ? disabledButton : {}) }}
                    onClick={() => saveWarrantyEdit(w.id)}
                    disabled={Boolean(busyAction)}
                  >
                    {busyAction === `saveWarranty-${w.id}` && <ButtonSpinner />}
                    {busyAction === `saveWarranty-${w.id}` ? "Saving..." : "Save Warranty"}
                  </button>
                  <button
                    style={dangerButton}
                    onClick={() => {
                      setEditingWarrantyId(null);
                      setEditingWarranty({ name: "", content: "" });
                    }}
                    disabled={Boolean(busyAction)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <strong>{w.name}</strong>
                <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                  {w.content}
                </p>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    style={actionButton}
                    onClick={() => {
                      setEditingWarrantyId(w.id);
                      setEditingWarranty({
                        name: w.name || "",
                        content: w.content || ""
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    style={dangerButton}
                    onClick={async () => {
                      await deleteDoc(doc(db, "proposalsPPDWarranties", w.id));
                      fetchWarranties();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      )}

      {/* ✅ CHANGED heading + collection */}
      <h3>Proposals PPD Terms</h3>

      <input
        placeholder="Term Name"
        value={newTerm.name}
        onChange={e => setNewTerm({ ...newTerm, name: e.target.value })}
      />

      <textarea
        placeholder="Term Content"
        rows={6}
        value={newTerm.content}
        onChange={e => setNewTerm({ ...newTerm, content: e.target.value })}
      />

      <button
        style={{ ...actionButton, marginTop: 8, ...(busyAction ? disabledButton : {}) }}
        onClick={addTerm}
        disabled={Boolean(busyAction)}
      >
        {busyAction === "addTerm" && <ButtonSpinner />}
        {busyAction === "addTerm" ? "Adding..." : "Add Term"}
      </button>

      <div style={{ marginTop: 12 }}>
        <button
          type="button"
          style={toggleButton}
          onClick={() => setShowTermContainers(prev => !prev)}
        >
          {showTermContainers ? "Hide Saved Terms" : "Show Saved Terms"}
        </button>
      </div>

      {showTermContainers && (
      <ul style={{ marginTop: 12 }}>
        {terms.length === 0 && (
          <li style={{ fontStyle: "italic", color: "#64748b" }}>
            No terms added yet.
          </li>
        )}

        {terms.map(t => (
          <li
            key={t.id}
            style={{
              border: "1px solid #1d4ed8",
              borderRadius: 10,
              padding: 12,
              marginBottom: 10,
              background: "#f8fafc",
            }}
          >
            {editingTermId === t.id ? (
              <>
                <input
                  style={{ ...inputStyle, width: "100%", minWidth: "100%" }}
                  value={editingTerm.name}
                  onChange={(e) =>
                    setEditingTerm(prev => ({ ...prev, name: e.target.value }))
                  }
                />
                <textarea
                  style={{ ...inputStyle, width: "100%", minWidth: "100%", marginTop: 8 }}
                  rows={6}
                  value={editingTerm.content}
                  onChange={(e) =>
                    setEditingTerm(prev => ({ ...prev, content: e.target.value }))
                  }
                />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    style={{ ...actionButton, ...(busyAction ? disabledButton : {}) }}
                    onClick={() => saveTermEdit(t.id)}
                    disabled={Boolean(busyAction)}
                  >
                    {busyAction === `saveTerm-${t.id}` && <ButtonSpinner />}
                    {busyAction === `saveTerm-${t.id}` ? "Saving..." : "Save Term"}
                  </button>
                  <button
                    style={dangerButton}
                    onClick={() => {
                      setEditingTermId(null);
                      setEditingTerm({ name: "", content: "" });
                    }}
                    disabled={Boolean(busyAction)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <strong>{t.name}</strong>
                <p style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>
                  {t.content}
                </p>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    style={actionButton}
                    onClick={() => {
                      setEditingTermId(t.id);
                      setEditingTerm({
                        name: t.name || "",
                        content: t.content || ""
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button
                    style={dangerButton}
                    onClick={async () => {
                      await deleteDoc(doc(db, "proposalsPPDTerms", t.id));
                      fetchTerms();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      )}

      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Delete "{confirmDelete.label}"?</h3>

            <button
              className="red-button"
              onClick={() => deleteDropdown(confirmDelete.id)}
            >
              Yes, Delete
            </button>

            <button
              className="green-button"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalsPPDDropdownsAdmin;